import { AppContext } from "01Data/AppData";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { FC, useContext, useState } from "react";

type DialogResult = "ok" | "cancel";

type DialogProps = {
  open: boolean;
  onClose: (result: DialogResult) => void;
};

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#f00",
  margin: "0.2rem",
}));

const UpdatePassDialogContent: FC<DialogProps> = ({ open, onClose }) => {
  const account = useContext(AppContext).account;
  const [showPwd, setShowPwd] = useState(false);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleApply = async () => {
    if (password === "") {
      setErrorMessage("現在のパスワードを入力してください。");
      return;
    }
    if (newPassword === "") {
      setErrorMessage("新しいパスワードを入力してください。");
      return;
    }
    if (confirmPassword === "") {
      setErrorMessage("確認用パスワードを入力してください。");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("新しいパスワードと確認用パスワードが一致しません。");
      return;
    }
    if (!newPassword.match(/^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d).{8,}$/)) {
      setErrorMessage("新しいパスワードの形式が不正です。");
      return;
    }
    try {
      await account.ChangePassword(password, newPassword);
      onClose("ok");
    } catch (error: any) {
      if (
        error.message.toLowerCase().indexOf("incorrect username or password") >=
        0
      ) {
        setErrorMessage("現在のパスワードが異なります。");
      } else {
        setErrorMessage(`パスワード変更が失敗しました。\n${error.message}`);
      }
    }
  };

  const handleClose = (event: {}, reason: "backdropClick") => {
    if (reason !== "backdropClick") {
      // 背景クリック時の close 抑止
      onClose("cancel");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="update-pass-dialog-title"
    >
      <DialogTitle id="update-pass-dialog-title">パスワード変更</DialogTitle>
      <DialogContent>
        <DialogContentText>
          半角英大文字・半角英小文字・数字を含む、８文字以上のパスワードを設定してください。
        </DialogContentText>
        <TextField
          autoFocus
          required
          fullWidth
          type={showPwd ? "text" : "password"}
          variant="filled"
          margin="normal"
          id="password"
          label="現在のパスワード"
          name="password"
          onChange={(e) => {
            setErrorMessage("");
            setPassword(e.target.value);
          }}
          inputProps={{ maxLength: 128 }}
          value={password}
        />
        <br />
        <TextField
          required
          fullWidth
          type={showPwd ? "text" : "password"}
          variant="filled"
          margin="normal"
          id="newPassword"
          label="新しいパスワード"
          name="newPassword"
          onChange={(e) => {
            setErrorMessage("");
            setNewPassword(e.target.value);
          }}
          inputProps={{ maxLength: 128 }}
          value={newPassword}
        />
        <br />
        <TextField
          required
          fullWidth
          type={showPwd ? "text" : "password"}
          variant="filled"
          margin="normal"
          id="confirmPassword"
          label="確認用パスワード"
          name="confirmPassword"
          onChange={(e) => {
            setErrorMessage("");
            setConfirmPassword(e.target.value);
          }}
          inputProps={{ maxLength: 128 }}
          value={confirmPassword}
        />
        <br />
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => {
                setShowPwd(e.target.checked);
              }}
            />
          }
          label="パスワードを表示します"
        />
        <ErrorText>{errorMessage ? errorMessage : "　"}</ErrorText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleApply}>更新する</Button>
        <Button onClick={() => onClose("cancel")}>ユーザー設定に戻る</Button>
      </DialogActions>
    </Dialog>
  );
};

export const useUpdatePassDialog = () => {
  const [open, setOpen] = useState(false);
  const [resolve, setResolve] = useState<(value: DialogResult) => void>();

  const openUpdatePassDialog = () => {
    setOpen(true);
    return new Promise<DialogResult>((resolve) => {
      setResolve(() => resolve);
    });
  };
  const onClose = (result: DialogResult) => {
    setOpen(false);
    if (resolve) {
      resolve(result);
    }
  };

  const UpdatePassDialog = () => (
    <UpdatePassDialogContent open={open} onClose={onClose} />
  );

  return {
    UpdatePassDialog,
    openUpdatePassDialog,
  };
};
